import { useCallback, useEffect, useRef, useState } from "react";
import { Route, Routes } from "react-router-dom";
import { Layout } from "./containers/Layout/Layout";
import { UserProfile } from "./containers/UserProfile/UserProfile";
import { UsersDashboard } from "./containers/UsersDashboard/UsersDashboard";
import { StudyPage } from "./containers/StudyPage/StudyPage";
import { Tasks } from "./containers/TasksPage/Tasks";
import { CalculationProperties } from "./containers/CalculationProperties/CalculationProperties";
import { Subtasks } from "./containers/SubtasksPage/Subtasks";
import { Subtask } from "./containers/Subtask/Subtask";
import { useDispatch, useSelector } from "react-redux";
import { fetchUsers } from "./store/usersReducer";
import { getQueuedSubtasks, getWorkers } from "./services/calculationsService";
import { SetAvailableWorkers, SetQueuedTimestamps } from "./actions/appInfoActions";
import { loadCalcParams } from "./containers/StudyPage/SaveLoadCalcButtons";
import { AppState } from "./store/configureStore";
import { fetchJustifications } from "./store/jusificationsReducer";
import { SetJustification } from "./actions/raoReportActions";
import { SetBlockedUpdate } from "./store/validationProcess";
import { fetchAppStage } from "./store/appStageReducer";
import { MandatoryPasswordReset } from "./containers/Login/Additional/MandatoryPasswordReset";
import "./App.css";

export const App = () => {
    const dispatch = useDispatch();
    const user = useSelector((state: AppState) => state.user);
    const justifications = useSelector((state: AppState) => state.justifications);
    const raoReport = useSelector((state: AppState) => state.raoReport);
    const [userGuid, setUserGuid] = useState("");
    const validationProcessState = useSelector((state: AppState) => state.validationProcess);
    const availableTimestampsOrScenarios = useSelector(
        (state: AppState) => state.validationProcess.availableTimestampsOrScenarios
    );
    const activeTimestamps = useSelector((state: AppState) => state.timestamps.timestamps);
    const activeTimestampsRef = useRef(activeTimestamps);
    useEffect(() => {
        activeTimestampsRef.current = activeTimestamps;
    }, [activeTimestamps]);

    const [combinedState, setCombinedState] = useState({
        process: validationProcessState.process,
        timestampsOrScenarios: availableTimestampsOrScenarios,
    });

    //use combined props to avoid multiple triggering
    useEffect(() => {
        setCombinedState({
            process: validationProcessState.process,
            timestampsOrScenarios: availableTimestampsOrScenarios,
        });
    }, [validationProcessState.process,
        availableTimestampsOrScenarios]);

    const fetchAppInfo = useCallback(async () => {
        try {
            const qs = await getQueuedSubtasks();
            dispatch(SetQueuedTimestamps(qs.totalRecordCount));
            const aw = await getWorkers();
            const apps = aw.flatMap(x => x.applications);
            dispatch(SetAvailableWorkers(apps.filter(x => x.name === "Perun4V").length));
        } catch (err) {
            console.error(err);
        }
    }, [dispatch]);

    useEffect(() => {
        fetchAppInfo();
        const intervalId = setInterval(() => {
            fetchAppInfo();
        }, 30000);
        return () => clearInterval(intervalId);
    }, [fetchAppInfo]);

    useEffect(() => {
        fetchAppStage(dispatch);
    }, [dispatch]);

    //use combined props to avoid multiple triggering
    useEffect(() => {
        fetchUsers(dispatch);
        if (userGuid && !validationProcessState.blockedUpdate) {
            loadCalcParams(
                dispatch,
                userGuid,
                user.tso!,
                combinedState.process,
                combinedState.timestampsOrScenarios,
                activeTimestampsRef.current
            );
        }
        fetchJustifications(dispatch);
    }, [combinedState,
        userGuid,
        dispatch,
        user.tso,
        validationProcessState.blockedUpdate])

    useEffect(() => {
        //Unblock IntraDay update if user has changed
        if (user.guid) {
            dispatch(SetBlockedUpdate(false));
            setUserGuid(user.guid);
        }
    }, [dispatch, user.guid]);

    useEffect(() => {
        if (raoReport.justification === "" && justifications.length > 0)
            dispatch(SetJustification(justifications[0].text as string));
    }, [justifications, dispatch, raoReport.justification]);

    return (
        <Layout>
            <Routes>
                <Route path="/Profile" element={<UserProfile />} />
                <Route path="/Study" element={<StudyPage />} />
                <Route
                    path="/Calculations/:calculationId/Tasks/:taskName/Subtasks"
                    element={<Subtasks />}
                />
                <Route path="/Calculations/Subtask/:subtaskGuid" element={<Subtask />} />
                <Route path="/Calculations/:calculationId/Tasks" element={<Tasks />} />
                <Route path="/Calculations/:guid" element={<CalculationProperties />} />
                <Route path="/Manage-Users" element={<UsersDashboard />} />
                <Route path="/*" element={<StudyPage />} />
            </Routes>
            <MandatoryPasswordReset />
        </Layout>
    );
};
